import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "next/navigation";
import { canLocationHostServices, filterServices, getUnavailableServiceIds } from "@/app/[tenant]/self-schedule/steps/services-step/components/helpers";
import { captureMarketingAttribution } from "@/lib/marketing-attribution";
import { buildBookingPreferenceAssignments, normalizeBookingPreferences, validateBookingPreferences } from "@/lib/self-schedule-booking-prefill";
import { type ProviderGroup } from "@/lib/self-schedule-provider-groups";
import { isVirtualServiceCart } from "@/lib/self-schedule-virtual-services";
import { flattenSelectedServices, flattenServiceAssignmentEntries, resolveServiceOccurrences } from "@/lib/service-addon-utils";
import { type BookingFeeMode, computeBookingFeeCents } from "@/lib/service-fees";
import { skipsSelfSchedulePatientTypeChoice } from "@/lib/tenant-flags";
import { toast } from "@/lib/use-toast";
import { PublicLocationDetail } from "@/types/location";
import { PublicProviderSummary } from "@/types/provider";
import { AvailabilityRequestAssignment, PublicBookingEvent } from "@/types/self-schedule";
import { PublicServiceDetail } from "@/types/service";
import { useBookingEvent } from "./use-booking-event";
import { useFrontPage } from "./use-front-page";
import { usePatientVerification } from "./use-patient-verification";
import { useProviderAssignment } from "./use-provider-assignment";
import { type SelfScheduleUrlPrefillRequest, useSelfScheduleUrlPrefill } from "./use-self-schedule-url-prefill";
import { requestData } from "@/api/api-config";
import { tenantQueryOptions } from "@/query/tenant";
import type { SelfScheduleStore } from "@/stores/self-schedule-store";
import { FIRST_AVAILABLE_PROVIDER_ID, LocationType, PatientType, SelfScheduleStep, isSelfScheduleComplete, useSelfScheduleStore } from "@/stores/self-schedule-store";

/** A service a `?serviceId=` link dropped into the cart before the patient looked. */
export interface SelfSchedulePrefilledService {
  id: string;
  name: string;
}
export type UseSelfScheduleReturn = Omit<SelfScheduleStore, "determineNextStep" | "lastUpdated" | "availabilitySelectionRevision" | "cancelServiceId" | "selectedLocationId" | "selectedProviderId"> & {
  requestedAssignments: AvailabilityRequestAssignment[];
  addonSelections: Record<string, string[]>;
  isServiceSelected: (serviceId: string) => boolean;
  /** Count of instances of a given service id currently in the cart. */
  getServiceInstanceCount: (serviceId: string) => number;
  determineNextStep: () => SelfScheduleStep | null;
  determinePreviousStep: () => SelfScheduleStep;
  previousStep: () => void;
  playNextStepAnimation: boolean;
  firstStep: SelfScheduleStep | null;
  shouldExposeLocationTypeChoice: boolean;
  startOver: () => void;
  /** One PublicServiceDetail per instance — duplicates of the same service repeat. */
  selectedServices: PublicServiceDetail[] | undefined;
  /** The location the patient chose, or the only one there is. */
  selectedLocation: PublicLocationDetail | undefined;
  /**
   * Every service in the cart is virtual: the visit is by video, and the
   * patient is never asked for a location.
   */
  isVirtualServiceBooking: boolean;
  /**
   * A virtual-service booking with no location chosen: availability searches
   * every location that can host it, and the visit is booked where the chosen
   * time was found.
   */
  searchesEveryLocation: boolean;
  /** Where the visit is booked: the chosen location, else the chosen time's. */
  bookingLocationId: string | null;
  bookingLocation: PublicLocationDetail | undefined;
  selectedProvider: PublicProviderSummary | undefined;
  /** Concrete providers aligned with selectedAvailabilityAssignments. */
  selectedAvailabilityProviders: (PublicProviderSummary | undefined)[];
  /** The provider assigned to the first service occurrence in the slot. */
  selectedAvailabilityProvider: PublicProviderSummary | undefined;
  hasData: boolean;
  /**
   * Provider groups for multi-provider self-scheduling. Empty unless services are
   * selected. `isMultiProviderSelection` is true only when there is more than
   * one group (no single provider covers all).
   */
  providerGroups: ProviderGroup[];
  isMultiProviderSelection: boolean;
  usesPerOccurrenceProviderSelection: boolean;
  /** Whether the appointment review step is satisfied (single- or multi-provider). */
  appointmentReviewComplete: boolean;
  /** Instances behind selectedServices, aligned 1:1 for provider flattening. */
  selectedServiceInstancesResolved: SelfScheduleStore["selectedServiceInstances"];
  numAllowedServices: number;
  /** Effective cap on total instances. Falls back to 0 when no service can be selected. */
  effectiveNumAllowedServices: number;
  availableProviders: PublicProviderSummary[] | undefined;
  shouldShowLocationBeforeService: boolean;
  /** When false, appointment durations are hidden from patients. Defaults true. */
  showServiceDuration: boolean;
  /** When true, the confirmation step offers a note for the provider. */
  patientNotesEnabled: boolean;
  /** When true, the provider step lists providers soonest-available first. */
  sortProvidersByAvailability: boolean;
  bookingFeeMode: BookingFeeMode;
  /** True when the current session arrived from a Reserve-with-Google deep link. */
  isRwgLanding: boolean;
  /**
   * Services a `?serviceId=` link put in the cart, in link order. The patient
   * never picked them, so the SERVICE step animates them into the selection
   * counter instead of leaving the cart silently pre-filled.
   */
  urlPrefilledServices: SelfSchedulePrefilledService[];
  /** Drops the prefill list once its arrival has been shown. */
  clearUrlPrefilledServices: () => void;
  /**
   * The booking event this session arrived on, once resolved. Pins the
   * calendar to its window and the cart to the services it offers.
   */
  bookingEvent: PublicBookingEvent | undefined;
  /** Authoritative waitlist link, cleared when continuing as an ordinary booking. */
  waitlistId: string | undefined;
};
export interface SelfScheduleRuntime {
  selfSchedule: UseSelfScheduleReturn;
  urlPrefillMessage: string | null;
  waitlistLinkState: "none" | "sign-in" | "loading" | "ready" | "error";
  continueWithoutWaitlist: () => void;
}

/**
 * Hook that provides all self-schedule state and methods for the booking flow.
 * Exposes all store functionality with automatic progression when conditions are met.
 */
export const useSelfScheduleRuntime = (): SelfScheduleRuntime => {
  const store = useSelfScheduleStore();
  const params = useParams<{
    tenant: string;
  }>();
  const skipPatientTypeChoice = skipsSelfSchedulePatientTypeChoice(params?.tenant);
  const {
    data: frontPageData,
    isLoading: isFrontPageLoading,
    isFetched: isFrontPageFetched
  } = useFrontPage();
  const {
    data: publicTenant,
    isLoading: isTenantLoading
  } = useQuery(tenantQueryOptions.public(params?.tenant));
  const hasWaitlistModule = publicTenant?.enabledModules?.includes("WAITLIST") ?? false;
  const numAllowedServices = publicTenant?.maxSelfScheduleServices ?? 1;
  const [playNextStepAnimation, setPlayNextStepAnimation] = useState(false);
  const {
    patient,
    signOutPatient,
    isResolvingSession
  } = usePatientVerification();
  const {
    selectedServiceInstances,
    resetAll,
    setSelectedLocationId,
    createdAppointmentId,
    currentStep: storedCurrentStep,
    selectedProviderId,
    selectedAvailabilityAssignments,
    availabilitySelectionRevision,
    confirmedServiceSelections,
    setConfirmedServiceSelections,
    setConfirmedAppointmentReview,
    selectedDateTime,
    confirmed,
    confirmedAppointmentReview,
    paymentSuccessful,
    setConfirmed,
    setSelectedDateTime,
    setCurrentStep,
    clearSelectedServices,
    setSelectedProviderId,
    setLocationType,
    locationType,
    patientType: storedPatientType,
    setPatientType,
    addSelectedService,
    setSelectedCategory,
    selectedLocationId,
    setPaymentSuccessful,
    setDismissedExistingAppointment,
    paymentMethodCollected,
    setPaymentMethodCollected,
    selectedAddons,
    setRwgToken,
    setRwgMerchantId,
    serviceInstanceProviderIds,
    bookingEventId,
    setBookingEventId
  } = store;

  // Track whether this session started from a Reserve-with-Google landing so
  // downstream logic (e.g. firstStep, determineNextStep) can skip the
  // patient-type / location-type choice screens and keep the SERVICE step
  // visible — Google's Sandbox review requires the landing page to surface
  // the entity name, address, and selectable services immediately.
  //
  // Initialize synchronously from the URL rather than deferring to the
  // effect below: if the front-page data is already cached, isFrontPageLoading
  // is false on the very first render and determineNextStep() runs before
  // the effect fires, which would auto-advance the RWG deep-link past the
  // SERVICE step before we ever set the flag.
  const [isRwgLanding, setIsRwgLanding] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    return params.get("rwg_token") !== null || params.get("utm_source") === "google_rwg";
  });
  // Reserve-with-Google lands on the service list without asking whether the
  // patient is new or existing, so treat it as Skip rather than leaving the
  // type null (which filters as a new patient and hides existing-only
  // services).
  const hasSelectedService = selectedServiceInstances.length > 0;
  const treatAsSkippedPatientType = skipPatientTypeChoice || isRwgLanding || hasSelectedService;

  // Match the Skip action before any consumer renders, including when an old
  // session saved a patient-type choice. Signed-in patients keep their status.
  const patientType = treatAsSkippedPatientType ? patient?.patientStatus ?? (hasSelectedService && !skipPatientTypeChoice && !isRwgLanding ? storedPatientType : null) ?? PatientType.ALL : storedPatientType;
  useEffect(() => {
    if (patient?.onSchedulingBlacklist) {
      toast({
        title: "On Scheduling Blacklist",
        description: "You are on the scheduling blacklist. Please contact the clinic to book an appointment.",
        variant: "error"
      });
      resetAll();
      signOutPatient();
      return;
    }
    if (treatAsSkippedPatientType) {
      // No patient-choice interaction in this flow. Persist the effective
      // value for store-based analytics without firing a selection event.
      if (storedPatientType !== patientType) {
        useSelfScheduleStore.setState({
          patientType
        });
      }
    } else if (patient) {
      setPatientType(patient.patientStatus);
    }
  }, [patient, patientType, storedPatientType, treatAsSkippedPatientType, setPatientType]);
  const [initialUrlParams] = useState(() => typeof window === "undefined" ? new URLSearchParams() : new URLSearchParams(window.location.search));
  const {
    tenant
  } = useParams<{
    tenant: string;
  }>();
  const [waitlistId, setWaitlistId] = useState(() => initialUrlParams.get("waitlistId"));
  const bookingScope = `${tenant}:${patient?.id ?? "anonymous"}`;
  // Retire only an appointment created in this mounted booking flow. A newly
  // opened waitlist link must still replace an unrelated persisted appointment.
  const appointmentAtMount = useRef(createdAppointmentId);
  const fulfilledWaitlistLink = createdAppointmentId !== null && createdAppointmentId !== appointmentAtMount.current && store.bookingScope === bookingScope;
  const hasVersionedPrefill = waitlistId !== null && !fulfilledWaitlistLink;
  useEffect(() => {
    if (!waitlistId || !fulfilledWaitlistLink) return;
    setWaitlistId(null);
    const url = new URL(window.location.href);
    url.searchParams.delete("waitlistId");
    window.history.replaceState(window.history.state, "", url.toString());
  }, [waitlistId, fulfilledWaitlistLink]);
  const [appliedBookingScope, setAppliedBookingScope] = useState<string | null>(null);
  const [bookingPrefillStatus, setBookingPrefillStatus] = useState<"pending" | "applied" | "invalid">("pending");
  const [bookingPrefillMessage, setBookingPrefillMessage] = useState<string | null>(null);
  const preferencesQuery = useQuery({
    queryKey: ["waitlist-booking-preferences", tenant, patient?.id, waitlistId],
    queryFn: async () => {
      const response = await requestData<unknown>(`patient/self-schedule/waitlist/${encodeURIComponent(waitlistId!)}/booking-preferences`, "get", {
        headers: {
          TENANT: tenant
        }
      }, false);
      const preferences = validateBookingPreferences(response);
      if (!preferences) throw new Error("Invalid waitlist booking preferences");
      return preferences;
    },
    enabled: Boolean(hasWaitlistModule && waitlistId?.trim() && patient?.id && !fulfilledWaitlistLink),
    retry: false,
    staleTime: 0,
    gcTime: 0
  });
  const bookingPreferences = patient && !preferencesQuery.isError && !preferencesQuery.isFetching ? preferencesQuery.data : undefined;
  useEffect(() => {
    if (hasVersionedPrefill && preferencesQuery.isError) {
      resetAll();
      setAppliedBookingScope(null);
    }
  }, [hasVersionedPrefill, preferencesQuery.isError, resetAll]);
  const previousBookingScope = useRef(bookingScope);
  useEffect(() => {
    if (previousBookingScope.current !== bookingScope) {
      previousBookingScope.current = bookingScope;
      setAppliedBookingScope(null);
      setBookingPrefillStatus("pending");
      setBookingPrefillMessage(null);
    }
  }, [bookingScope]);
  // Ordinary MFA adopts this tenant's anonymous draft; known owners and
  // waitlist prefills must never hand their cart to another patient or tenant.
  useEffect(() => {
    // A pending claim is not a sign-out: retain its persisted draft until the
    // session resolves and supplies the authoritative patient identity.
    if (isResolvingSession) return;
    if (store.bookingScope !== bookingScope) {
      const adoptsAnonymousDraft = !hasVersionedPrefill && Boolean(patient?.id) && store.bookingScope === `${tenant}:anonymous`;
      if (!adoptsAnonymousDraft && (store.bookingScope !== null || hasVersionedPrefill)) resetAll();
      useSelfScheduleStore.setState({
        bookingScope
      });
    }
  }, [bookingScope, store.bookingScope, isResolvingSession, resetAll, hasVersionedPrefill, patient?.id, tenant]);
  const waitlistLinkState: SelfScheduleRuntime["waitlistLinkState"] = !hasVersionedPrefill ? "none" : publicTenant && !hasWaitlistModule ? "error" : !patient ? "sign-in" : !waitlistId?.trim() || preferencesQuery.isError || appliedBookingScope === bookingScope && bookingPrefillStatus === "invalid" ? "error" : !preferencesQuery.isFetching && appliedBookingScope === bookingScope && bookingPrefillStatus === "applied" ? "ready" : "loading";
  const continueWithoutWaitlist = useCallback(() => {
    resetAll();
    useSelfScheduleStore.setState({
      bookingScope
    });
    setWaitlistId(null);
    setBookingPrefillMessage(null);
    const url = new URL(window.location.href);
    url.searchParams.delete("waitlistId");
    window.location.replace(url.toString());
  }, [resetAll, bookingScope]);
  const requestedPrefill = useMemo<SelfScheduleUrlPrefillRequest>(() => ({
    providerId: hasVersionedPrefill ? null : initialUrlParams.get("providerId"),
    locationId: hasVersionedPrefill ? null : initialUrlParams.get("locationId")
  }), [hasVersionedPrefill, initialUrlParams]);
  const legacyPrefillMessage = useSelfScheduleUrlPrefill({
    requestedContext: requestedPrefill,
    selectedProviderId,
    selectedLocationId,
    frontPageData,
    isLoading: isFrontPageLoading,
    isResolved: isFrontPageFetched
  });
  const urlPrefillMessage = bookingPrefillMessage ?? legacyPrefillMessage;

  // Handle URL parameters for location and service autofill
  useEffect(() => {
    if (typeof window !== "undefined") {
      // Snapshot marketing attribution BEFORE this effect strips utm/click
      // params off the URL below (idempotent; first-touch-wins).
      captureMarketingAttribution();
      // Keep the opaque ID in the URL through sign-in, refresh and back navigation.
      const urlParams = new URLSearchParams(initialUrlParams);
      const locationIdFromUrl = urlParams.get("locationId");
      const serviceIdsFromUrl = urlParams.getAll("serviceId").filter(serviceId => serviceId.trim().length > 0);
      const categoryIdFromUrl = urlParams.get("categoryId");
      const providerIdFromUrl = urlParams.get("providerId");
      const bookingEventIdFromUrl = urlParams.get("bookingEventId");
      const rwgTokenFromUrl = urlParams.get("rwg_token");
      const utmSourceFromUrl = urlParams.get("utm_source");
      const cameFromRwg = rwgTokenFromUrl !== null || utmSourceFromUrl === "google_rwg";
      if (hasVersionedPrefill || locationIdFromUrl || serviceIdsFromUrl.length > 0 || categoryIdFromUrl || providerIdFromUrl || bookingEventIdFromUrl) {
        resetAll();
        if (hasVersionedPrefill) urlParams.delete("serviceId");

        // Set after resetAll so the wipe doesn't take the event with it. The
        // id is all that is kept — the event itself is fetched from it.
        if (bookingEventIdFromUrl) {
          setBookingEventId(bookingEventIdFromUrl);
          urlParams.delete("bookingEventId");
        }
        if (locationIdFromUrl) {
          urlParams.delete("locationId");
        }
        if (providerIdFromUrl) {
          urlParams.delete("providerId");
        }
        if (categoryIdFromUrl) {
          if (!hasVersionedPrefill) setSelectedCategory(categoryIdFromUrl);
          urlParams.delete("categoryId");
        }
        if (cameFromRwg) {
          setIsRwgLanding(true);
          if (rwgTokenFromUrl) {
            setRwgToken(rwgTokenFromUrl);
          }
          urlParams.delete("rwg_token");
          urlParams.delete("utm_source");
        }

        // Clear the parameters from URL after setting them
        const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : "") + window.location.hash;
        window.history.replaceState({}, "", newUrl);
      }
    }
    // This effect intentionally consumes the immutable mount-time snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps, react-compiler/react-compiler
  }, []);
  const hasAppliedRequestedPrefill = useRef(false);
  useEffect(() => {
    if (hasVersionedPrefill || !frontPageData || hasAppliedRequestedPrefill.current) return;
    hasAppliedRequestedPrefill.current = true;
    const providerId = requestedPrefill.providerId;
    const locationId = requestedPrefill.locationId;
    if (providerId && frontPageData.providers.some(provider => provider.id === providerId)) {
      setSelectedProviderId(providerId);
    }
    if (locationId && frontPageData.locations.some(location => location.id === locationId)) {
      setSelectedLocationId(locationId);
      if (isRwgLanding) setRwgMerchantId(locationId);
    }
  }, [frontPageData, isRwgLanding, requestedPrefill, hasVersionedPrefill, setRwgMerchantId, setSelectedLocationId, setSelectedProviderId]);
  const {
    urlPrefilledServices,
    setUrlPrefilledServices,
    clearUrlPrefilledServices
  } = store;
  useEffect(() => {
    if (!hasVersionedPrefill || !patient || !bookingPreferences || appliedBookingScope === bookingScope || !frontPageData || isFrontPageLoading || isTenantLoading) return;
    setAppliedBookingScope(bookingScope);
    const draft = normalizeBookingPreferences(bookingPreferences, frontPageData, numAllowedServices);
    if (!draft) {
      resetAll();
      useSelfScheduleStore.setState({
        bookingScope
      });
      setBookingPrefillStatus("invalid");
      setBookingPrefillMessage("This waitlist link is no longer available.");
      return;
    }
    store.applyBookingPreferences(draft);
    setBookingPrefillStatus("applied");
    if (draft.droppedChoices) setBookingPrefillMessage("Some choices in this booking link are no longer available. Please review your services, location and provider preferences below.");
  }, [bookingPreferences, hasVersionedPrefill, patient, appliedBookingScope, bookingScope, resetAll, frontPageData, isFrontPageLoading, isTenantLoading, numAllowedServices, store.applyBookingPreferences]);
  const appliedLegacyServices = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined" || hasVersionedPrefill || appliedLegacyServices.current || isTenantLoading || isFrontPageLoading || !frontPageData?.services) return;
    const urlParams = new URLSearchParams(window.location.search);
    const serviceIdsFromUrl = initialUrlParams.getAll("serviceId").filter(serviceId => serviceId.trim().length > 0);
    if (serviceIdsFromUrl.length === 0) return;
    appliedLegacyServices.current = true;
    const servicesById = new Map(frontPageData.services.map(service => [service.id, service]));
    const selectedServiceIds = new Set<string>();
    const prefilled: SelfSchedulePrefilledService[] = [];
    serviceIdsFromUrl.filter(serviceId => {
      const service = servicesById.get(serviceId);
      if (!service) return false;
      if (service.allowMultipleBookings || !selectedServiceIds.has(serviceId)) {
        selectedServiceIds.add(serviceId);
        return true;
      }
      return false;
    }).slice(0, numAllowedServices).forEach(serviceId => {
      addSelectedService(serviceId);
      const service = servicesById.get(serviceId);
      if (service) prefilled.push({
        id: service.id,
        name: service.name
      });
    });
    setUrlPrefilledServices(prefilled);
    urlParams.delete("serviceId");
    const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : "");
    window.history.replaceState({}, "", newUrl);
  }, [addSelectedService, hasVersionedPrefill, initialUrlParams, frontPageData?.services, isFrontPageLoading, isTenantLoading, numAllowedServices, setUrlPrefilledServices]);
  const shouldExposePatientTypeChoice = !treatAsSkippedPatientType && frontPageData?.shouldExposePatientTypeChoice === true;
  const shouldExposeLocationTypeChoice = frontPageData?.shouldExposeLocationTypeChoice === true;
  // Location-first is a distance heuristic, so only physical locations count.
  const hasMultipleBookableLocations = (frontPageData?.locations?.filter(location => location.isArchived !== true && location.isVirtual !== true).length ?? 0) > 1;
  const clinicHasVirtualServices = frontPageData?.services?.some(service => service.isVirtual) ?? false;
  // A patient who chose Telehealth at a clinic with virtual services picks the
  // service first: a virtual service is booked without a location, and a clinic
  // with no virtual location would have none to offer here.
  const shouldShowLocationBeforeService = (frontPageData?.shouldShowLocationBeforeService === true || hasMultipleBookableLocations) && !(clinicHasVirtualServices && locationType === LocationType.VIRTUAL);
  // Default to showing durations; only hide when the tenant explicitly opts out.
  const showServiceDuration = frontPageData?.showServiceDuration !== false;
  const patientNotesEnabled = frontPageData?.selfSchedulePatientNotesEnabled === true;
  const sortProvidersByAvailability = frontPageData?.selfScheduleProviderSort === "SOONEST_AVAILABLE";
  // A persisted patient-type screen is not valid when the choice is skipped.
  // Normalize synchronously rather than mounting it and advancing in an effect.
  const currentStep = treatAsSkippedPatientType && storedCurrentStep === SelfScheduleStep.PATIENT_TYPE ? isFrontPageLoading ? null : isRwgLanding ? SelfScheduleStep.SERVICE : shouldExposeLocationTypeChoice ? SelfScheduleStep.LOCATION_TYPE : shouldShowLocationBeforeService ? SelfScheduleStep.LOCATION : SelfScheduleStep.SERVICE : storedCurrentStep;
  // How stacked services combine into the appointment's upfront fee.
  const bookingFeeMode: BookingFeeMode = frontPageData?.bookingFeeMode === "HIGHEST_SERVICE" ? "HIGHEST_SERVICE" : "PER_SERVICE";

  // Materialize one PublicServiceDetail per instance so duplicates show up as
  // separate rows in the itinerary, time picker, etc.
  const selectedServices = useMemo(() => {
    if (!frontPageData?.services) return undefined;
    const byId = new Map(frontPageData.services.map(s => [s.id, s]));
    return selectedServiceInstances.map(inst => byId.get(inst.id)).filter((s): s is PublicServiceDetail => Boolean(s));
  }, [frontPageData?.services, selectedServiceInstances]);
  const isVirtualServiceBooking = isVirtualServiceCart(selectedServices);
  // A video visit can take any clinic's time. A clinic the patient or their
  // link picked (the location step, ?locationId=, Google Reserve) narrows it,
  // so that clinic gets the visit and its fee.
  const searchesEveryLocation = isVirtualServiceBooking && selectedLocationId === null;
  // The chosen time's location counts only with that time.
  const bookingLocationId = searchesEveryLocation ? selectedDateTime !== null ? store.selectedSlotLocationId : null : selectedLocationId;
  // A virtual-service booking needs no location before it picks a time.
  const locationSatisfied = selectedLocationId !== null || isVirtualServiceBooking;

  // The instances behind `selectedServices`, in the same order and filtered by
  // the SAME predicate (service exists in the catalog), so a provider array
  // built from these lines up positionally with flattenServiceIds(selectedServices).
  const selectedServiceInstancesResolved = useMemo(() => {
    if (!frontPageData?.services) return [];
    const known = new Set(frontPageData.services.map(s => s.id));
    return selectedServiceInstances.filter(inst => known.has(inst.id));
  }, [frontPageData?.services, selectedServiceInstances]);

  // Parents plus their selected add-ons — the same collection Checkout prices.
  // Add-ons carry their own booking fees, so every skip/advance decision below
  // has to see them; a zero-fee parent with a fee-bearing add-on would
  // otherwise skip checkout and leave the appointment RESERVED and unpaid.
  const bookingFeeServices = useMemo(() => flattenSelectedServices(selectedServices ?? [], selectedAddons), [selectedServices, selectedAddons]);

  // Bound the tenant cap by eligible service capacity. A repeatable service
  // can fill the entire cap; otherwise each eligible service contributes one.
  const computeEffectiveNumAllowedServices = useCallback((providerId: string | null) => {
    const filterProviderId = providerId === FIRST_AVAILABLE_PROVIDER_ID ? null : providerId;
    const unavailableServiceIds = getUnavailableServiceIds({
      services: frontPageData?.services,
      providers: frontPageData?.providers,
      selectedServices,
      selectedServiceIds: selectedServiceInstances.map(instance => instance.id),
      selectedLocationId,
      selectedProviderId: filterProviderId
    });
    const selectableServices = filterServices(frontPageData?.services, {
      searchQuery: "",
      selectedCategory: "all",
      locations: frontPageData?.locations,
      locationType,
      selectedLocationId: shouldShowLocationBeforeService ? selectedLocationId : null,
      selectedProviderId: filterProviderId,
      patientType,
      categories: []
    }).filter(service => !service.onlyBookableOverThePhone && !unavailableServiceIds.has(service.id));
    if (selectableServices.some(service => service.allowMultipleBookings)) {
      return numAllowedServices;
    }
    return Math.min(numAllowedServices, selectableServices.length);
  }, [frontPageData?.services, frontPageData?.locations, locationType, shouldShowLocationBeforeService, selectedLocationId, patientType, numAllowedServices, frontPageData?.providers, selectedServices, selectedServiceInstances]);
  const effectiveNumAllowedServices = useMemo(() => computeEffectiveNumAllowedServices(selectedProviderId), [computeEffectiveNumAllowedServices, selectedProviderId]);
  const {
    data: bookingEvent,
    isError: isBookingEventError
  } = useBookingEvent();

  // An expired or withdrawn link drops back to ordinary booking rather than
  // stranding the patient on a dead page.
  useEffect(() => {
    if (!isBookingEventError || !bookingEventId) return;
    setBookingEventId(null);
    toast({
      title: "This event link has expired",
      description: "You can still book an appointment below.",
      variant: "warning"
    });
  }, [isBookingEventError, bookingEventId, setBookingEventId]);

  // An event that names a provider books with that provider, so the choice is
  // made up front instead of offering a picker with one entry.
  useEffect(() => {
    if (!bookingEvent?.providerId || selectedProviderId !== null) return;
    const provider = frontPageData?.providers.find(candidate => candidate.id === bookingEvent.providerId);
    if (!provider) return;
    setSelectedProviderId(provider.id, `${provider.firstName} ${provider.lastName}`.trim());
  }, [bookingEvent, frontPageData?.providers, selectedProviderId, setSelectedProviderId]);
  const singleAvailableLocation = useMemo(() => {
    const locations = frontPageData?.locations ?? [];
    return locations.length === 1 ? locations[0] : undefined;
  }, [frontPageData?.locations]);
  const selectedLocation = useMemo(() => {
    return frontPageData?.locations?.find(location => location.id === selectedLocationId);
  }, [frontPageData?.locations, selectedLocationId]);
  const bookingLocation = useMemo(() => frontPageData?.locations?.find(location => location.id === bookingLocationId), [frontPageData?.locations, bookingLocationId]);
  useEffect(() => {
    if (currentStep !== null || !singleAvailableLocation || selectedLocationId !== null) return;
    setSelectedLocationId(singleAvailableLocation.id, singleAvailableLocation.name);
    // A clinic with virtual services offers telehealth at its one clinic too,
    // so the patient still says which they want.
    if (singleAvailableLocation.isVirtual || !clinicHasVirtualServices) {
      setLocationType(singleAvailableLocation.isVirtual ? LocationType.VIRTUAL : LocationType.IN_PERSON);
    }
  }, [clinicHasVirtualServices, currentStep, singleAvailableLocation, selectedLocationId, setLocationType, setSelectedLocationId]);

  // Who performs each service — groups, preference reconciliation, and review
  // completion all live in one place.
  const {
    skipAppointmentReview,
    providerGroups,
    isMultiProviderSelection,
    usesPerOccurrenceProviderSelection,
    appointmentReviewComplete,
    selectedProvider,
    selectedAvailabilityProviders,
    selectedAvailabilityProvider,
    availableProviders
  } = useProviderAssignment({
    frontPageData,
    isFrontPageLoading: isFrontPageLoading || hasVersionedPrefill && bookingPrefillStatus === "pending",
    selectedServices,
    selectedServiceInstances,
    selectedAvailabilityAssignments,
    serviceInstanceProviderIds,
    providerPreferenceMode: store.providerPreferenceMode,
    selectedProviderId,
    selectedLocationId,
    confirmedAppointmentReview,
    createdAppointmentId
  });

  // Locations that can host everything currently in the cart.
  const bookableLocationsForServices = useMemo(() => {
    if (!selectedServices?.length) return [];
    return (frontPageData?.locations ?? []).filter(location => canLocationHostServices(location, selectedServices));
  }, [frontPageData?.locations, selectedServices]);

  // Infer location info from the selected services. The location-first flow
  // asks up front, but a URL prefill (?serviceId=) can fill the cart before the
  // patient ever reaches the LOCATION step — with a single bookable location
  // left there is nothing to ask, so resolve it and move on.
  // What this effect filled in itself (as opposed to a URL pin, the
  // single-clinic default, or the patient's own choice) and what it replaced.
  const inferredLocationRef = useRef<{
    id: string;
    previousId: string | null;
  } | null>(null);
  const inferredLocationTypeRef = useRef<{
    type: LocationType;
    previous: LocationType | null;
  } | null>(null);
  useEffect(() => {
    const impliedLocationTypes = new Set(bookableLocationsForServices.map(location => location.isVirtual ? LocationType.VIRTUAL : LocationType.IN_PERSON));
    // A cart of virtual services is telehealth, and picks no location: it is
    // searched at every one.
    const impliedLocationType = isVirtualServiceBooking ? LocationType.VIRTUAL : impliedLocationTypes.size === 1 ? Array.from(impliedLocationTypes)[0] ?? null : null;
    const onlyLocation = !isVirtualServiceBooking && bookableLocationsForServices.length === 1 ? bookableLocationsForServices[0] : undefined;

    // In the service-first flow the cart is what implied these. Once the
    // patient edits it so it no longer does, release them so they stop
    // narrowing the service list. Track the values left after a release so a
    // straight swap to a new inference restores the original, not the old one.
    let currentLocationId = selectedLocationId;
    let currentLocationType = locationType;
    if (!shouldShowLocationBeforeService) {
      const inferredLocation = inferredLocationRef.current;
      if (inferredLocation && onlyLocation?.id !== inferredLocation.id) {
        inferredLocationRef.current = null;
        if (inferredLocation.id === currentLocationId) {
          currentLocationId = inferredLocation.previousId;
          setSelectedLocationId(currentLocationId);
        }
      }
      const inferredLocationType = inferredLocationTypeRef.current;
      if (inferredLocationType && impliedLocationType !== inferredLocationType.type) {
        inferredLocationTypeRef.current = null;
        if (inferredLocationType.type === currentLocationType) {
          currentLocationType = inferredLocationType.previous;
          setLocationType(currentLocationType);
        }
      }
    }

    // A cart of virtual services is telehealth at a picked location too.
    if (shouldShowLocationBeforeService && currentLocationId !== null && !isVirtualServiceBooking) return;
    if (!isVirtualServiceBooking && bookableLocationsForServices.length === 0) return;
    if (impliedLocationType !== null && impliedLocationType !== currentLocationType) {
      inferredLocationTypeRef.current = {
        type: impliedLocationType,
        previous: currentLocationType
      };
      setLocationType(impliedLocationType);
    }
    if (onlyLocation && onlyLocation.id !== currentLocationId) {
      inferredLocationRef.current = {
        id: onlyLocation.id,
        previousId: currentLocationId
      };
      setSelectedLocationId(onlyLocation.id, onlyLocation.name);
    }
  }, [bookableLocationsForServices, isVirtualServiceBooking, locationType, selectedLocationId, setLocationType, setSelectedLocationId, shouldShowLocationBeforeService]);
  useEffect(() => {
    setPaymentSuccessful(selectedServices ? bookingFeeServices.every(service => computeBookingFeeCents(service) <= 0) : false);
  }, [selectedServices, bookingFeeServices, setPaymentSuccessful]);
  useEffect(() => {
    // A virtual service is by video wherever it is booked. And where the clinic
    // has virtual services, a clinic location says nothing about which the
    // patient wants.
    if (!selectedLocation || isVirtualServiceBooking) return;
    if (selectedLocation.isVirtual) setLocationType(LocationType.VIRTUAL);else if (!clinicHasVirtualServices) setLocationType(LocationType.IN_PERSON);
  }, [clinicHasVirtualServices, isVirtualServiceBooking, selectedLocation, setLocationType]);
  const firstStep = useMemo(() => {
    if (isFrontPageLoading) return null;
    // On a Reserve-with-Google landing, jump straight to the service picker so
    // the patient immediately sees the selectable service list Google requires
    // on the landing page. The location is already carried in the URL, and
    // patient type is treated as skipped (ALL) until the patient signs in.
    if (isRwgLanding) return SelfScheduleStep.SERVICE;
    if (shouldExposePatientTypeChoice) return SelfScheduleStep.PATIENT_TYPE;
    if (shouldExposeLocationTypeChoice) return SelfScheduleStep.LOCATION_TYPE;
    if (shouldShowLocationBeforeService) return SelfScheduleStep.LOCATION;
    return SelfScheduleStep.SERVICE;
  }, [isRwgLanding, shouldExposePatientTypeChoice, shouldExposeLocationTypeChoice, shouldShowLocationBeforeService, isFrontPageLoading]);
  const determineNextStep = useCallback((): SelfScheduleStep | null => {
    // Waitlist enrollment is terminal, independent of booking/payment state.
    if (currentStep === SelfScheduleStep.WAITLIST_COMPLETE) return SelfScheduleStep.WAITLIST_COMPLETE;
    if (isFrontPageLoading || waitlistLinkState !== "none" && waitlistLinkState !== "ready") return null;

    // Auto-advance when only 1 service can be selected (either by config or
    // by filtering). Suppress the URL-preselect auto-completion on RWG
    // landings — otherwise a service-specific Google deep link on a
    // single-select tenant jumps straight past the SERVICE step and
    // Google's crawler (and the patient) never see the services list that
    // the RWG landing spec requires. Explicit user confirmation still
    // advances the flow normally.
    const completedServiceStep = !isRwgLanding && effectiveNumAllowedServices === 1 && selectedServiceInstances.length === 1 || confirmedServiceSelections === true;

    // Reserve-with-Google landings skip the patient-type / location-type
    // choice screens the same way firstStep does — Google requires the
    // patient to land on the service list. Without this, a state change
    // that recomputes the next step would still route them back into those
    // gates. Location comes from the RwG URL param; patient type is treated
    // as skipped until the patient signs in.
    if (!isRwgLanding && shouldExposePatientTypeChoice && patientType === null) {
      return SelfScheduleStep.PATIENT_TYPE;
    }
    if (!isRwgLanding && shouldExposeLocationTypeChoice && locationType === null) {
      return SelfScheduleStep.LOCATION_TYPE;
    }

    // Once an appointment exists, its reserved event is authoritative. Provider
    // preferences may be reconciled against a newer catalog, but that must not
    // reopen review/time selection or pair a newly selected slot with the old
    // createdAppointmentId. Resume the existing event's payment directly.
    if (createdAppointmentId !== null && completedServiceStep && bookingLocationId !== null && selectedDateTime !== null && patient?.id != null && confirmed === true) {
      return paymentSuccessful ? SelfScheduleStep.BOOKING_COMPLETE : SelfScheduleStep.CHECKOUT;
    }

    // Check if payment method is needed
    // Skip payment method step if there's a booking fee - payment will be collected in checkout
    const bookingFeeAmount = bookingFeeServices.reduce((acc, service) => acc + computeBookingFeeCents(service), 0);
    const hasBookingFee = !!bookingFeeAmount;
    const needsPaymentMethod = selectedServices?.length && patient?.id && selectedServices?.some(service => service.creditCardRequired || service.collectCreditCard) && !paymentMethodCollected && !patient?.hasPaymentMethod && !hasBookingFee; // Skip if there's a booking fee - will collect in checkout

    if (completedServiceStep && bookingLocationId !== null && appointmentReviewComplete && selectedDateTime !== null && patient?.id != null && confirmed === true && !needsPaymentMethod) {
      return SelfScheduleStep.SECURE_APPOINTMENT;
    }
    if (completedServiceStep && bookingLocationId !== null && appointmentReviewComplete && selectedDateTime !== null && patient?.id != null && confirmed === true && needsPaymentMethod) {
      return SelfScheduleStep.PAYMENT_METHOD;
    }
    if (completedServiceStep && bookingLocationId !== null && appointmentReviewComplete && selectedDateTime !== null) {
      return SelfScheduleStep.CONFIRMATION;
    }
    if (completedServiceStep && locationSatisfied && appointmentReviewComplete) {
      return SelfScheduleStep.TIME;
    }

    // When location comes before service, show LOCATION first then SERVICE
    if (shouldShowLocationBeforeService) {
      if (!locationSatisfied) {
        return SelfScheduleStep.LOCATION;
      }
      if (!completedServiceStep) {
        return SelfScheduleStep.SERVICE;
      }
      if (!appointmentReviewComplete) {
        return SelfScheduleStep.APPOINTMENT_REVIEW;
      }
    } else {
      if (completedServiceStep && !locationSatisfied) {
        return SelfScheduleStep.LOCATION;
      }
      if (completedServiceStep && locationSatisfied && !appointmentReviewComplete) {
        return SelfScheduleStep.APPOINTMENT_REVIEW;
      }
    }

    // Fallback: unreachable in practice because the branches above cover all
    // combinations exhaustively. Kept as a defensive default so the function
    // always returns a step even if future changes introduce a gap.
    return shouldShowLocationBeforeService ? SelfScheduleStep.LOCATION : SelfScheduleStep.SERVICE;
  }, [currentStep, waitlistLinkState, createdAppointmentId, selectedServiceInstances, bookingLocationId, locationSatisfied, selectedProviderId, selectedDateTime, skipAppointmentReview, appointmentReviewComplete, patient?.id, confirmed, paymentSuccessful, shouldExposePatientTypeChoice, shouldExposeLocationTypeChoice, patientType, locationType, shouldShowLocationBeforeService, isFrontPageLoading, selectedServices, bookingFeeServices, paymentMethodCollected, confirmedServiceSelections, effectiveNumAllowedServices, isRwgLanding]);

  // In the service-first flow the LOCATION step is never shown when there is
  // only one place to book — the auto-select effects fill it in. Back must skip
  // it too: landing on LOCATION would reselect that location and snap the
  // patient straight forward again.
  const isLocationStepSkipped = !shouldShowLocationBeforeService && (singleAvailableLocation !== undefined || isVirtualServiceBooking || bookableLocationsForServices.length === 1);
  const determinePreviousStep = useCallback((): SelfScheduleStep => {
    // Helper: the step that comes right before the location-vs-service pair.
    // When no preceding step exists (no patient-type or location-type choice),
    // returns the first step of the main flow itself (LOCATION or SERVICE).
    // This is intentional — `previousStep()` guards with
    // `if (previous === currentStep) return;` and the back button is hidden
    // when `currentStep === firstStep`.
    const stepBeforeMainFlow = (): SelfScheduleStep => {
      if (shouldExposeLocationTypeChoice) return SelfScheduleStep.LOCATION_TYPE;
      if (shouldExposePatientTypeChoice) return SelfScheduleStep.PATIENT_TYPE;
      return shouldShowLocationBeforeService ? SelfScheduleStep.LOCATION : SelfScheduleStep.SERVICE;
    };
    switch (currentStep) {
      case SelfScheduleStep.WAITLIST_COMPLETE:
        return SelfScheduleStep.WAITLIST_COMPLETE;
      case SelfScheduleStep.PATIENT_TYPE:
        return SelfScheduleStep.PATIENT_TYPE;
      case SelfScheduleStep.LOCATION_TYPE:
        return shouldExposePatientTypeChoice ? SelfScheduleStep.PATIENT_TYPE : SelfScheduleStep.LOCATION_TYPE;
      case SelfScheduleStep.LOCATION:
        if (shouldShowLocationBeforeService) {
          return stepBeforeMainFlow();
        }
        return SelfScheduleStep.SERVICE;
      case SelfScheduleStep.SERVICE:
        if (shouldShowLocationBeforeService) {
          return SelfScheduleStep.LOCATION;
        }
        return stepBeforeMainFlow();
      case SelfScheduleStep.APPOINTMENT_REVIEW:
        return shouldShowLocationBeforeService || isLocationStepSkipped ? SelfScheduleStep.SERVICE : SelfScheduleStep.LOCATION;
      case SelfScheduleStep.TIME:
        return skipAppointmentReview ? shouldShowLocationBeforeService || isLocationStepSkipped ? SelfScheduleStep.SERVICE : SelfScheduleStep.LOCATION : SelfScheduleStep.APPOINTMENT_REVIEW;
      case SelfScheduleStep.CONFIRMATION:
        return SelfScheduleStep.TIME;
      case SelfScheduleStep.PAYMENT_METHOD:
        return SelfScheduleStep.CONFIRMATION;
      default:
        return shouldShowLocationBeforeService ? SelfScheduleStep.LOCATION : SelfScheduleStep.SERVICE;
    }
  }, [currentStep, isLocationStepSkipped, shouldExposeLocationTypeChoice, shouldExposePatientTypeChoice, shouldShowLocationBeforeService, skipAppointmentReview]);

  // Suppress auto-progression on the render immediately after navigating
  // backward, so the user isn't snapped back to a later step.
  const backwardNavRef = useRef(false);
  const previousStep = useCallback(() => {
    const previous = determinePreviousStep();
    if (previous === currentStep) return;
    if (previous === SelfScheduleStep.PATIENT_TYPE) {
      if (usesPerOccurrenceProviderSelection) store.clearReviewProviderPreferences();
      signOutPatient();
      setDismissedExistingAppointment(false);
      setPatientType(null);
      setSelectedLocationId(null);
      setLocationType(null);
      return;
    }
    // Clear the step that is one level "deeper" than the previous step.
    // In location-first flow, SERVICE is deeper than LOCATION, so going
    // back from SERVICE/PROVIDER/TIME clears services; in the normal flow
    // LOCATION is deeper than SERVICE, so going back clears the location.
    const clearOneStepBack = () => {
      if (usesPerOccurrenceProviderSelection) {
        // Returning to the editable services must not erase imported occurrence
        // preferences, addon ownership or stable anchors.
        store.clearReviewProviderPreferences();
        return;
      }
      if (shouldShowLocationBeforeService) {
        setConfirmedServiceSelections(false);
        clearSelectedServices();
        setSelectedProviderId(null);
      } else if (isLocationStepSkipped) {
        // Back to SERVICE past the skipped LOCATION. Keep the cart so the
        // patient can edit it, unless a one-service cart would count as
        // complete on its own and bounce them forward again. A provider picked
        // on review would otherwise keep narrowing the service list; one from
        // a ?providerId= link or a booking event is that link's constraint, so
        // it stays.
        const keptProviderId = selectedProviderId !== null && (selectedProviderId === requestedPrefill.providerId || selectedProviderId === bookingEvent?.providerId) ? selectedProviderId : null;
        setConfirmedServiceSelections(false);
        setSelectedProviderId(keptProviderId);
        if (!isRwgLanding && computeEffectiveNumAllowedServices(keptProviderId) === 1) {
          clearSelectedServices();
        }
      } else {
        if (selectedProviderId !== requestedPrefill.providerId && selectedProviderId !== bookingEvent?.providerId) {
          setSelectedProviderId(null);
        }
        setSelectedLocationId(null);
      }
    };
    switch (currentStep) {
      case SelfScheduleStep.LOCATION_TYPE:
        setPatientType(null);
        break;
      case SelfScheduleStep.SERVICE:
        if (usesPerOccurrenceProviderSelection) store.clearReviewProviderPreferences();
        if (shouldShowLocationBeforeService) {
          setConfirmedServiceSelections(false);
          if (!usesPerOccurrenceProviderSelection) clearSelectedServices();
          setSelectedLocationId(null);
          setLocationType(null);
        } else {
          setLocationType(null);
        }
        break;
      case SelfScheduleStep.LOCATION:
        if (usesPerOccurrenceProviderSelection) store.clearReviewProviderPreferences();
        if (shouldShowLocationBeforeService) {
          setSelectedLocationId(null);
          setLocationType(null);
        } else {
          setConfirmedServiceSelections(false);
          if (!usesPerOccurrenceProviderSelection) clearSelectedServices();
        }
        break;
      case SelfScheduleStep.APPOINTMENT_REVIEW:
        clearOneStepBack();
        break;
      case SelfScheduleStep.TIME:
        setSelectedDateTime(null);
        if (skipAppointmentReview) {
          clearOneStepBack();
        } else {
          setConfirmedAppointmentReview(false);
        }
        break;
      case SelfScheduleStep.CONFIRMATION:
        // Keep selectedDateTime so the user sees their previous time highlighted,
        // but invalidate the review confirmation when they leave this step.
        setConfirmed(false);
        break;
      case SelfScheduleStep.PAYMENT_METHOD:
        setConfirmed(false);
        break;
      default:
        break;
    }
    backwardNavRef.current = true;
    setCurrentStep(previous, "backward");
  }, [currentStep, determinePreviousStep, setSelectedDateTime, setConfirmed, createdAppointmentId, skipAppointmentReview, setCurrentStep, clearSelectedServices, setSelectedProviderId, setSelectedLocationId, setPatientType, setPaymentMethodCollected, setConfirmedServiceSelections, setConfirmedAppointmentReview, shouldShowLocationBeforeService, selectedProviderId, requestedPrefill.providerId, usesPerOccurrenceProviderSelection, store.clearReviewProviderPreferences, isLocationStepSkipped, isRwgLanding, computeEffectiveNumAllowedServices, bookingEvent?.providerId, setLocationType]);

  // Track whether we're handling a browser back navigation (popstate)
  const isHandlingPopStateRef = useRef(false);
  const hasInitializedHistoryRef = useRef(false);

  // Push/replace history state on step changes so the Back button navigates steps
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (currentStep === null) return;
    if (firstStep === null || currentStep === firstStep) return;
    const state = {
      ...(window.history.state ?? {}),
      selfScheduleFlow: true,
      step: currentStep
    } as Record<string, unknown>;
    if (!hasInitializedHistoryRef.current) {
      window.history.replaceState(state, "");
      hasInitializedHistoryRef.current = true;
      return;
    }

    // Avoid pushing a new entry if we're already handling a popstate-driven change
    if (isHandlingPopStateRef.current) return;
    window.history.pushState(state, "");
  }, [currentStep, firstStep]);

  // Listen for browser back/forward and map it to previous step
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handlePopState = () => {
      isHandlingPopStateRef.current = true;
      previousStep();
      // allow a microtask tick before re-enabling history pushes
      queueMicrotask(() => {
        isHandlingPopStateRef.current = false;
      });
    };

    // Do not intercept browser navigation on the first step
    if (firstStep === null || currentStep === firstStep || currentStep === SelfScheduleStep.CHECKOUT || isSelfScheduleComplete(currentStep) || currentStep === SelfScheduleStep.SECURE_APPOINTMENT) {
      window.removeEventListener("popstate", handlePopState);
      return;
    }
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [previousStep, currentStep, firstStep]);

  // Step tracking removed - only tracking specific selection events

  // Automatic progression logic
  useEffect(() => {
    if (backwardNavRef.current) {
      backwardNavRef.current = false;
      return; // skip auto-advance on the render right after backward navigation
    }
    if (playNextStepAnimation) return;
    const nextStepToGo = determineNextStep();
    if (nextStepToGo === null) return;
    if (nextStepToGo !== currentStep) {
      if (currentStep !== null) {
        setPlayNextStepAnimation(true);
        setTimeout(() => {
          setPlayNextStepAnimation(false);
        }, 300);
      }
      setCurrentStep(nextStepToGo, "forward");
    }
  }, [selectedServiceInstances, selectedProviderId, selectedLocationId, selectedDateTime, availabilitySelectionRevision, serviceInstanceProviderIds, confirmedAppointmentReview, skipAppointmentReview, currentStep, determineNextStep, confirmed, createdAppointmentId, patient?.id, paymentSuccessful, playNextStepAnimation, patientType, isFrontPageLoading, paymentMethodCollected]);

  // Precompute the per-service-id count once per change to keep card-grid
  // lookups O(1) and the function reference stable across renders.
  // Pair every ordered service occurrence with its preference. Automatically
  // assigned services and add-ons softly prefer the anchor service's concrete
  // provider so the itinerary stays with one provider whenever possible.
  const requestedAssignments = useMemo(() => {
    if (!selectedServices) return [];
    if (usesPerOccurrenceProviderSelection) return buildBookingPreferenceAssignments(selectedServiceInstancesResolved, selectedServices, selectedAddons, serviceInstanceProviderIds, store.addonProviderIds, store.preferredProviderEntryKeys);
    const serviceById = new Map(selectedServices.map(s => [s.id, s]));
    const instanceProviderIds = isMultiProviderSelection ? serviceInstanceProviderIds : Object.fromEntries(selectedServiceInstancesResolved.map(instance => [instance.instanceId, selectedProviderId ?? FIRST_AVAILABLE_PROVIDER_ID]));
    const entries = flattenServiceAssignmentEntries(selectedServiceInstancesResolved, serviceById, selectedAddons, instanceProviderIds);
    const groupKeyByInstanceId = new Map(providerGroups.flatMap(group => group.instances.map(({
      instanceId
    }) => [instanceId, group.key] as const)));
    const anchorIndexByGroupKey = new Map<string, number>();
    entries.forEach(({
      sourceInstanceId
    }, index) => {
      const groupKey = groupKeyByInstanceId.get(sourceInstanceId);
      if (groupKey && !anchorIndexByGroupKey.has(groupKey)) {
        anchorIndexByGroupKey.set(groupKey, index);
      }
    });
    return entries.map(({
      assignment,
      sourceInstanceId
    }, index) => {
      const providerId = assignment.providerId && assignment.providerId !== FIRST_AVAILABLE_PROVIDER_ID ? assignment.providerId : null;
      const groupKey = groupKeyByInstanceId.get(sourceInstanceId);
      const preferredProviderAssignmentIndex = isMultiProviderSelection ? groupKey ? anchorIndexByGroupKey.get(groupKey) : undefined : 0;
      return {
        ...assignment,
        providerId,
        ...(providerId === null && preferredProviderAssignmentIndex != null && preferredProviderAssignmentIndex !== index ? {
          preferredProviderAssignmentIndex
        } : {})
      };
    });
  }, [usesPerOccurrenceProviderSelection, store.addonProviderIds, store.preferredProviderEntryKeys, isMultiProviderSelection, providerGroups, selectedServices, selectedServiceInstancesResolved, selectedAddons, serviceInstanceProviderIds, selectedProviderId]);
  const addonSelections = useMemo(() => Object.fromEntries(resolveServiceOccurrences(selectedServices ?? [], selectedAddons).filter(({
    addonIds
  }) => addonIds.length > 0).map(({
    occurrenceKey,
    addonIds
  }) => [occurrenceKey, addonIds])), [selectedServices, selectedAddons]);
  const instanceCountsById = useMemo(() => {
    const counts = new Map<string, number>();
    for (const inst of selectedServiceInstances) {
      counts.set(inst.id, (counts.get(inst.id) ?? 0) + 1);
    }
    return counts;
  }, [selectedServiceInstances]);
  const isServiceSelected = useMemo(() => {
    return (serviceId: string) => instanceCountsById.has(serviceId);
  }, [instanceCountsById]);
  const getServiceInstanceCount = useMemo(() => {
    return (serviceId: string) => instanceCountsById.get(serviceId) ?? 0;
  }, [instanceCountsById]);
  const {
    lastUpdated: _lastUpdated,
    availabilitySelectionRevision: _availabilitySelectionRevision,
    selectedLocationId: _selectedLocationId,
    ...publicStore
  } = store;
  const hasData = useMemo(() => {
    return !!selectedServices?.length || !!selectedLocation || !!selectedProvider || !!selectedAvailabilityProvider || !!selectedDateTime || !!locationType;
  }, [selectedServices, selectedLocation, selectedProvider, selectedAvailabilityProvider, selectedDateTime, locationType]);
  return {
    selfSchedule: {
      ...publicStore,
      requestedAssignments,
      addonSelections,
      currentStep,
      patientType,
      selectedServices,
      selectedLocation,
      isVirtualServiceBooking,
      searchesEveryLocation,
      bookingLocationId,
      bookingLocation,
      selectedProvider,
      selectedAvailabilityProviders,
      selectedAvailabilityProvider,
      isServiceSelected,
      getServiceInstanceCount,
      numAllowedServices,
      effectiveNumAllowedServices,
      hasData,
      availableProviders,
      providerGroups,
      isMultiProviderSelection,
      usesPerOccurrenceProviderSelection,
      appointmentReviewComplete,
      selectedServiceInstancesResolved,
      determineNextStep,
      determinePreviousStep,
      previousStep,
      playNextStepAnimation,
      firstStep,
      shouldExposeLocationTypeChoice,
      shouldShowLocationBeforeService,
      showServiceDuration,
      patientNotesEnabled,
      sortProvidersByAvailability,
      bookingFeeMode,
      isRwgLanding,
      urlPrefilledServices,
      clearUrlPrefilledServices,
      bookingEvent: bookingEvent ?? undefined,
      waitlistId: waitlistLinkState === "ready" ? waitlistId ?? undefined : undefined,
      startOver: () => {
        resetAll();
        if (hasVersionedPrefill) {
          setAppliedBookingScope(null);
          setBookingPrefillStatus("pending");
        }
      }
    },
    urlPrefillMessage,
    waitlistLinkState,
    continueWithoutWaitlist
  };
};
export const useSelfSchedule = () => {
  return useSelfScheduleRuntime().selfSchedule;
};